import { Pressable, StyleSheet, View, type GestureResponderEvent } from "react-native";
import Svg, { Circle } from "react-native-svg";

import { pictureScaleOf } from "../pictureScale";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import type { UiTheme } from "../theme";
import type { PointSelection } from "./pointSelection";

// A line read by a tap (PG-64): the whole plot is one tap target and picks
// the point nearest in x. The selected point wears a ring, a shape and not
// a colour. A hold does nothing (the no-op `onLongPress` cancels the press),
// a drag scrolls the page. The screen reader moves the selection through the
// plot's adjustable (`plotA11yProps`), so this layer is hidden from it.

/** One point a tap can pick: `i` is its index in the selection. */
export type TapTarget = { i: number; x: number };

type Props = {
  theme: UiTheme;
  /** Pixel x of every pickable point, in plot coordinates. */
  targets: ReadonlyArray<TapTarget>;
  /** Where the selected point sits, in plot coordinates. */
  marker?: { x: number; y: number };
  selection: PointSelection;
  width: number;
  height: number;
  /** The plot's left margin (a picture's inset). */
  left?: number;
};

// A ring around the widest dot, in ink like the bars' marker.
const RING_RADIUS = 8;
const RING_STROKE = 3;
/** How far the ring reaches past its point: a plot keeps this much margin
 *  left and right, so a ring on the first or last point is never cut. */
export const MARKER_EXTENT = RING_RADIUS + RING_STROKE / 2;

/** Native hands the touch's x in the target; RNW hands a mouse event. */
function pressX(e: GestureResponderEvent): number | undefined {
  const n = e.nativeEvent as { locationX?: number; offsetX?: number };
  return n.locationX ?? n.offsetX;
}

const ignoreHold = () => {};

export function nearestTarget(targets: ReadonlyArray<TapTarget>, x: number): number | undefined {
  let best: TapTarget | undefined;
  for (const t of targets) {
    if (best === undefined || Math.abs(t.x - x) < Math.abs(best.x - x)) best = t;
  }
  return best?.i;
}

export function ChartTapLayer({ theme, targets, marker, selection, width, height, left = 0 }: Props) {
  const k = pictureScaleOf(theme);
  const r = RING_RADIUS * k;
  const onPress = (e: GestureResponderEvent) => {
    const x = pressX(e);
    if (x === undefined) return;
    const i = nearestTarget(targets, x);
    if (i !== undefined) selection.select(i);
  };
  return (
    <View style={[styles.layer, { width: width + 2 * left, height }]} pointerEvents="box-none">
      {marker ? (
        <Svg width={width + 2 * left} height={height} style={styles.layer} pointerEvents="none">
          <Circle
            cx={left + marker.x}
            cy={marker.y}
            r={r}
            fill="none"
            stroke={theme.palette.ink}
            strokeWidth={RING_STROKE * k}
          />
        </Svg>
      ) : null}
      <Pressable
        testID="chart-tap-layer"
        onPress={onPress}
        onLongPress={ignoreHold}
        // RNW's Pressable is a tab stop unless `tabIndex` says otherwise
        // (`focusable` is not read there): the plot's slider is the only one.
        tabIndex={-1}
        focusable={false}
        accessible={false}
        {...hiddenFromScreenReader()}
        style={[styles.layer, { left, width, height }]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  layer: { position: "absolute", top: 0, left: 0 },
});
